import { createSelector } from 'reselect';
// Import services.
import {
	DateService,
} from '@app/services';
// Import enumerations.
import {
	AppLinkType,
	AppMode,
} from '@app/enumerations';
// Import state types.
import {
	AppState,
} from '@app/redux/states';
// Import api models.
import {
	AppLink,
	DisqualifiersText,
	PossibleCorrespondenceRecvdOrSent,
	PossibleCorrespondenceSubject,
	PossibleCorrespondenceType,
	PossibleCorrespondenceWho,
	PossibleDocCategory,
	PossibleProducerAgency,
	PossibleState,
	ValidNewBusinessInceptionDateBoundaries,
} from '@app/models/api';
// Import app models.
import {
	AppNotification,
} from '@app/models/app';
// Import interfaces.
import {
	IUserData,
} from '@app/interfaces';

// Fetches the app state.
const getAppState = (state: AppState): AppState => {
	return state;
}

// Fetches the user data.
const getUserData = (state: AppState, userData: IUserData): IUserData => {
	return userData;
}

// Get App Mode Selector.
const makeIsAppModeActiveSelector = () => createSelector(
	[
		(state: AppState, appMode: AppMode) => appMode,
		getAppState
	],
	(appMode: AppMode, state: AppState): boolean => {
		return state.appMode[appMode] ?? false
	}
);

// Get Disqualifiers Text Selector.
const makeGetDisqualifiersTextSelector = () => createSelector(
	[getAppState],
	(appState: AppState): DisqualifiersText | undefined => {
		return appState.disqualifiersText;
	}
);

// Get Main Navigation Links Selector
const makeGetMainNavigationLinksSelector = () => createSelector(
	[getAppState, getUserData],
	(appState: AppState, userData: IUserData): AppLink[] => {
		return appState.mainNavigationLinks.filter((appLink: AppLink) => {
			let selectLink: boolean = true;
			let appLinkType: AppLinkType = appLink.appLinkType !== null ? appLink.appLinkType : AppLinkType.NONE;

			if ((appLinkType & AppLinkType.ADMIN_ONLY) != 0) {
				selectLink = userData.isUserAdmin && selectLink;
			}
			if ((appLinkType & AppLinkType.RESTRICTED_FOR_NPBE_ADMIN) != 0) {
				selectLink = !userData.isUserNPBEAdmin && selectLink;
            }
			if ((appLinkType & AppLinkType.VISIBLE_ON_LOGIN) != 0
				&& (appLinkType & AppLinkType.VISIBLE_ON_LOGOUT) != 0) {
				selectLink = selectLink;
			}
			else if ((appLinkType & AppLinkType.VISIBLE_ON_LOGIN) != 0) {
				selectLink = userData.isUserDataLoaded && selectLink;
			}
			else if ((appLinkType & AppLinkType.VISIBLE_ON_LOGOUT) != 0) {
				selectLink = !userData.isUserDataLoaded && selectLink;
			}

			return selectLink;
		});
	}
);

// Get Possible Correspondence Recvd Or Sent Selector.
const makeGetPossibleCorrespondenceRecvdOrSentSelector = () => createSelector(
	[getAppState],
	(appState: AppState): PossibleCorrespondenceRecvdOrSent[] => {
		return appState.possibleCorrespondenceRecvdOrSent;
	}
);

// Get Possible Correspondence Subjects Selector.
const makeGetPossibleCorrespondenceSubjectsSelector = () => createSelector(
	[getAppState],
	(appState: AppState): PossibleCorrespondenceSubject[] => {
		return appState.possibleCorrespondenceSubjects;
	}
);

// Get Possible Correspondence Types Selector.
const makeGetPossibleCorrespondenceTypesSelector = () => createSelector(
	[getAppState],
	(appState: AppState): PossibleCorrespondenceType[] => {
		return appState.possibleCorrespondenceTypes;
	}
);

// Get Possible Correspondence Who Selector.
const makeGetPossibleCorrespondenceWhoSelector = () => createSelector(
	[getAppState],
	(appState: AppState): PossibleCorrespondenceWho[] => {
		return appState.possibleCorrespondenceWho;
	}
);

// Get Possible Doc Categories Selector.
const makeGetPossibleDocCategoriesSelector = () => createSelector(
	[getAppState],
	(appState: AppState): PossibleDocCategory[] => {
		return appState.possibleDocCategories;
	}
);

// Get Possible Producer Agencies Selector.
const makeGetPossibleProducerAgenciesSelector = () => createSelector(
	[getAppState],
	(appState: AppState): PossibleProducerAgency[] => {
		return appState.possibleProducerAgencies;
	}
);

// Get Possible Producer Agencies In Ascending Order Selector.
const makeGetPossibleProducerAgenciesInAscendingOrderSelector = () => createSelector(
	[makeGetPossibleProducerAgenciesSelector()],
	(possibleProducerAgencies: PossibleProducerAgency[]): PossibleProducerAgency[] => {
		return possibleProducerAgencies.sort((possibleProducerAgency1: PossibleProducerAgency, possibleProducerAgency2: PossibleProducerAgency) => {
			let possibleProducerAgency1Id: number = possibleProducerAgency1.id ?? 0;
			let possibleProducerAgency2Id: number = possibleProducerAgency2.id ?? 0;
			return possibleProducerAgency1Id - possibleProducerAgency2Id;
		});
	}
);


// Get Possible States Selector.
const makeGetPossibleStatesSelector = () => createSelector(
	[getAppState],
	(appState: AppState): PossibleState[] => {
		return appState.possibleStates;
	}
);

// Get Valid New Business Inception Date Boundaries Selector.
const makeGetValidNewBusinessInceptionDateBoundariesSelector = () => createSelector(
	[getAppState],
	(appState: AppState): ValidNewBusinessInceptionDateBoundaries | undefined => {
		return appState.validNewBusinessInceptionDateBoundaries;
	}
);

// Get Valid New Business Maximum Inception Date Boundary Selector.
const makeGetValidNewBusinessMaximumInceptionDateBoundarySelector = () => createSelector(
	[makeGetValidNewBusinessInceptionDateBoundariesSelector()],
	(validNewBusinessInceptionDateBoundaries: ValidNewBusinessInceptionDateBoundaries | undefined): Date => {
		return validNewBusinessInceptionDateBoundaries !== undefined && validNewBusinessInceptionDateBoundaries.latestDateAllowed !== null
			? DateService.getInstance().normalizeDate(validNewBusinessInceptionDateBoundaries.latestDateAllowed)
			: new Date();
	}
);

// Get Valid New Business Minimum Inception Date Boundary Selector.
const makeGetValidNewBusinessMinimumInceptionDateBoundarySelector = () => createSelector(
	[makeGetValidNewBusinessInceptionDateBoundariesSelector()],
	(validNewBusinessInceptionDateBoundaries: ValidNewBusinessInceptionDateBoundaries | undefined): Date => {
		return validNewBusinessInceptionDateBoundaries !== undefined && validNewBusinessInceptionDateBoundaries.earliestDateAllowed !== null
			? DateService.getInstance().normalizeDate(validNewBusinessInceptionDateBoundaries.earliestDateAllowed)
			: new Date();
	}
);

// Get Notifications Selector.
const makeGetNotificationsSelector = () => createSelector(
	[getAppState],
	(appState: AppState): AppNotification[] => {
		return appState.notifications;
	}
);

// Are Main Navigation Links Loaded Selector
const makeAreMainNavigationLinksLoadedSelector = () => createSelector(
	[getAppState],
	(appState: AppState): boolean => {
		return appState.areMainNavigationLinksLoaded;
	}
);

// Are Possible Correspondence Recvd Or Sent Loaded Selector.
const makeArePossibleCorrespondenceRecvdOrSentLoadedSelector = () => createSelector(
	[getAppState],
	(appState: AppState): boolean => {
		return appState.arePossibleCorrespondenceRecvdOrSentLoaded;
	}
);

// Are Possible Correspondence Subjects Loaded Selector.
const makeArePossibleCorrespondenceSubjectsLoadedSelector = () => createSelector(
	[getAppState],
	(appState: AppState): boolean => {
		return appState.arePossibleCorrespondenceSubjectsLoaded;
	}
);

// Are Possible Correspondence Types Loaded Selector.
const makeArePossibleCorrespondenceTypesLoadedSelector = () => createSelector(
	[getAppState],
	(appState: AppState): boolean => {
		return appState.arePossibleCorrespondenceTypesLoaded;
	}
);

// Are Possible Correspondence Who Loaded Selector.
const makeArePossibleCorrespondenceWhoLoadedSelector = () => createSelector(
	[getAppState],
	(appState: AppState): boolean => {
		return appState.arePossibleCorrespondenceWhoLoaded;
	}
);

// Are Possible Doc Categories Loaded Selector.
const makeArePossibleDocCategoriesLoadedSelector = () => createSelector(
	[getAppState],
	(appState: AppState): boolean => {
		return appState.arePossibleDocCategoriesLoaded;
	}
);

// Are Possible Producer Agencies Loaded Selector.
const makeArePossibleProducerAgenciesLoadedSelector = () => createSelector(
	[getAppState],
	(appState: AppState): boolean => {
		return appState.arePossibleProducerAgenciesLoaded;
	}
);

// Are possible states loaded selector.
const makeArePossibleStatesLoadedSelector = () => createSelector(
	[getAppState],
	(appState: AppState): boolean => {
		return appState.arePossibleStatesLoaded;
	}
);

// Are Valid New Business Inception Date Boundaries LoadedSelector.
const makeAreValidNewBusinessInceptionDateBoundariesLoadedSelector = () => createSelector(
	[getAppState],
	(appState: AppState): boolean => {
		return appState.areValidNewBusinessInceptionDateBoundariesLoaded;
	}
);

// Export composed methods.
export {
	getAppState,
};

// export selectors.
export {
	makeIsAppModeActiveSelector,
	makeGetDisqualifiersTextSelector,
	makeGetMainNavigationLinksSelector,
	makeGetPossibleCorrespondenceRecvdOrSentSelector,
	makeGetPossibleCorrespondenceSubjectsSelector,
	makeGetPossibleCorrespondenceTypesSelector,
	makeGetPossibleCorrespondenceWhoSelector,
	makeGetPossibleDocCategoriesSelector,
	makeGetPossibleProducerAgenciesSelector,
	makeGetPossibleProducerAgenciesInAscendingOrderSelector,
	makeGetPossibleStatesSelector,
	makeGetValidNewBusinessInceptionDateBoundariesSelector,
	makeGetValidNewBusinessMaximumInceptionDateBoundarySelector,
	makeGetValidNewBusinessMinimumInceptionDateBoundarySelector,
	makeGetNotificationsSelector,
	makeAreMainNavigationLinksLoadedSelector,
	makeArePossibleCorrespondenceRecvdOrSentLoadedSelector,
	makeArePossibleCorrespondenceSubjectsLoadedSelector,
	makeArePossibleCorrespondenceTypesLoadedSelector,
	makeArePossibleCorrespondenceWhoLoadedSelector,
	makeArePossibleDocCategoriesLoadedSelector,
	makeArePossibleProducerAgenciesLoadedSelector,
	makeArePossibleStatesLoadedSelector,
	makeAreValidNewBusinessInceptionDateBoundariesLoadedSelector,
};